import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import './CategoryMaster.css';

const STATUS_OPTIONS = [
    { label: 'Active',   value: 'Active'   },
    { label: 'Inactive', value: 'Inactive' }
];

const ModuleMenuDialog = ({ visible, menuData, groupOptions, onHide, onSave }) => {
    const [form, setForm]     = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!visible) return;
        setErrors({});
        if (menuData) {
            setForm({
                module_menu_id:  menuData.module_menu_id,
                module_name:     menuData.module_name     || '',
                module_group_id: menuData.module_group_id || null,
                link:            menuData.link            || '',
                icon:            menuData.icon            || '',
                status:          menuData.status          || 'Active'
            });
        } else {
            setForm({ module_name: '', module_group_id: null, link: '', icon: '', status: 'Active' });
        }
    }, [visible, menuData]);

    const update = (field, val) => {
        setForm((prev) => ({ ...prev, [field]: val }));
        if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    };

    const validate = () => {
        const errs = {};
        if (!form.module_name?.trim())  errs.module_name     = 'Module Name is required';
        if (!form.module_group_id)      errs.module_group_id = 'Module Group is required';
        if (!form.link?.trim())         errs.link            = 'Link is required';
        if (!form.icon?.trim())         errs.icon            = 'Icon is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        onSave({
            module_menu_id:  form.module_menu_id || null,
            module_name:     form.module_name.trim(),
            module_group_id: form.module_group_id,
            link:            form.link.trim(),
            icon:            form.icon.trim(),
            status:          form.status,
            updated_by:      localStorage.getItem('userid') || ''
        });
    };

    const isEdit = !!menuData?.module_menu_id;

    const dialogHeader = (
        <div className="sub-dialog-header">
            <span className="sub-dialog-header-icon">
                <i className="pi pi-list" />
            </span>
            <div>
                <div className="sub-dialog-header-text">
                    {isEdit ? 'Edit Module Menu' : 'Add Module Menu'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                    {isEdit ? 'Update the module menu details below' : 'Enter new module menu details below'}
                </div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={onHide} />
            <Button label={isEdit ? 'Update' : 'Save'} icon="pi pi-check" className="sub-dialog-save" onClick={handleSave} />
        </div>
    );

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: '520px' }}
            modal
            position="center"
            footer={dialogFooter}
            onHide={onHide}
            draggable={false}
            resizable={false}
            className="sub-master-dialog p-fluid"
        >
            <div className="sub-form-grid">

                {/* Module Name — full width */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-tag sub-field-label-icon" />
                        Module Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputText
                        value={form.module_name || ''}
                        onChange={(e) => update('module_name', e.target.value)}
                        placeholder="e.g. Dashboard"
                        className={`w-full${errors.module_name ? ' p-invalid' : ''}`}
                    />
                    {errors.module_name && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.module_name}
                        </span>
                    )}
                </div>

                {/* Module Group — full width */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-th-large sub-field-label-icon" />
                        Module Group <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <Dropdown
                        value={form.module_group_id}
                        options={groupOptions}
                        onChange={(e) => update('module_group_id', e.value)}
                        placeholder="Select Module Group"
                        filter
                        className={`w-full${errors.module_group_id ? ' p-invalid' : ''}`}
                    />
                    {errors.module_group_id && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.module_group_id}
                        </span>
                    )}
                </div>

                {/* Link */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-link sub-field-label-icon" />
                        Link <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputText
                        value={form.link || ''}
                        onChange={(e) => update('link', e.target.value)}
                        placeholder="e.g. /dashboard"
                        className={`w-full${errors.link ? ' p-invalid' : ''}`}
                    />
                    {errors.link && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.link}
                        </span>
                    )}
                </div>

                {/* Icon */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-star sub-field-label-icon" />
                        Icon <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputText
                        value={form.icon || ''}
                        onChange={(e) => update('icon', e.target.value)}
                        placeholder="e.g. pi pi-home"
                        className={`w-full${errors.icon ? ' p-invalid' : ''}`}
                    />
                    {errors.icon && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.icon}
                        </span>
                    )}
                </div>

                {/* Status — full width */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-circle sub-field-label-icon" />
                        Status
                    </label>
                    <Dropdown
                        value={form.status || 'Active'}
                        options={STATUS_OPTIONS}
                        onChange={(e) => update('status', e.value)}
                        className="w-full"
                    />
                </div>

            </div>
        </Dialog>
    );
};

export default ModuleMenuDialog;
